import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, MessageCircle, Bookmark, Share2, ImageIcon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { usePosts } from '../contexts/PostContext';
import FactRating from '../components/posts/FactRating';
import CommentSection from '../components/posts/CommentSection';

const PostDetail: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { posts, likePost, savePost, addComment, deleteComment } = usePosts();
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  const post = posts.find(p => p.id === postId);

  useEffect(() => {
    if (!post) {
      // Post not found, redirect to home
      navigate('/home');
    }
  }, [post, navigate]);

  if (!post) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  const handleLike = async () => {
    if (!currentUser) return;
    try {
      await likePost(post.id);
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleSave = async () => {
    if (!currentUser) return;
    try {
      await savePost(post.id);
    } catch (error) {
      console.error('Error saving post:', error);
    }
  };

  const handleShare = async () => {
    const postUrl = `${window.location.origin}/home/posts/${post.id}`;
    const shareData = {
      title: post.subject,
      text: `Check out this post: ${post.subject}`,
      url: postUrl
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(postUrl);
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Error sharing:', error);
        try {
          await navigator.clipboard.writeText(postUrl);
          alert('Link copied to clipboard!');
        } catch (clipboardError) {
          console.error('Error copying to clipboard:', clipboardError);
        }
      }
    }
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    console.log(`Failed to load image for post: ${post.id} URL: ${post.photo}`);
    setImageError(true);
    setImageLoading(false);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center text-gray-400 hover:text-white transition-colors mb-6"
      >
        <ArrowLeft className="h-5 w-5 mr-2" />
        <span>Back</span>
      </button>

      <article className="bg-[#272727] rounded-xl overflow-hidden">
        {/* Post Header */}
        <div className="p-6 flex items-center space-x-4">
          <img 
            src={post.userPhotoURL} 
            alt={post.username}
            className="w-12 h-12 rounded-full object-cover border border-gray-700"
          />
          <div>
            <h3 className="font-medium text-white">{post.username}</h3>
            <p className="text-sm text-gray-400">
              {formatDistanceToNow(new Date(post.createdAt), { addSuffix: true })}
            </p>
          </div>
        </div>

        {/* Post Image */}
        <div className="relative aspect-video bg-gray-800">
          {imageError ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
              <div className="text-center text-gray-400">
                <ImageIcon className="h-16 w-16 mx-auto mb-4" />
                <p>Image unavailable</p>
              </div>
            </div>
          ) : (
            <>
              {imageLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                  <div className="animate-spin rounded-full h-12 w-12 border-2 border-white border-t-transparent"></div>
                </div>
              )}
              <img 
                src={post.photo}
                alt={post.subject}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-200 ${
                  imageLoading ? 'opacity-0' : 'opacity-100'
                }`}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            </>
          )}
        </div>

        {/* Post Content */}
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-white">{post.subject}</h1>
            {post.topic && (
              <span className="text-sm text-purple-400">#{post.topic}</span>
            )}
          </div>

          <div className="prose prose-invert max-w-none mb-6">
            <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">
              {post.content}
            </p>
          </div>

          {/* Post Actions */}
          <div className="flex items-center justify-between border-t border-gray-700 pt-4">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={handleLike}
                className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 rounded-lg transition-colors ${
                  post.isLiked
                    ? 'text-red-500 bg-red-500/10'
                    : 'text-gray-400 hover:text-red-500 hover:bg-red-500/10'
                }`}
              >
                <Heart className={`h-5 w-5 ${post.isLiked ? 'fill-current' : ''}`} />
                <span>{post.likeCount}</span>
              </button>

              <div className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 rounded-lg text-gray-400">
                <MessageCircle className="h-5 w-5" />
                <span>{post.commentCount}</span>
              </div>

              <button
                onClick={handleSave}
                className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 rounded-lg transition-colors ${
                  post.isSaved
                    ? 'text-yellow-500 bg-yellow-500/10'
                    : 'text-gray-400 hover:text-yellow-500 hover:bg-yellow-500/10'
                }`}
              >
                <Bookmark className={`h-5 w-5 ${post.isSaved ? 'fill-current' : ''}`} />
              </button>

              <button
                onClick={handleShare}
                className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 rounded-lg text-gray-400 hover:text-green-500 hover:bg-green-500/10 transition-colors"
              >
                <Share2 className="h-5 w-5" />
              </button>
            </div>

            <FactRating rating={post.factRating} postId={post.id} />
          </div>
        </div>
      </article>

      {/* Comments Section */}
      <div className="mt-8 bg-[#272727] rounded-xl p-6">
        <CommentSection
          comments={post.comments}
          postId={post.id}
          onAddComment={addComment}
          onDeleteComment={deleteComment}
        />
      </div>
    </div>
  );
};

export default PostDetail;